/**
 * SidebarUniversalSearch — persistent universal search bar at the top of the left ribbon.
 *
 * Boolean full-text search over ALL of the user's threads and documents (titles + full
 * content) via GET /api/search. ⌘K / Ctrl+K focuses it from anywhere; a gear opens
 * filters (content type, date range, space, topic). Results render in a portal
 * dropdown with full keyboard navigation.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  universalSearchService,
  countActiveFilters,
  DEFAULT_SEARCH_FILTERS,
  type UniversalSearchFilters,
  type UniversalSearchResult,
} from "@/services/universalSearchService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Search,
  Settings,
  X,
  Loader2,
  MessageSquare,
  FileText,
  HelpCircle,
  RotateCcw,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { UNIVERSAL_SEARCH_FOCUS_EVENT, focusUniversalSearch } from "@/lib/universalSearch";

const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
const shortcutLabel = isMac ? "⌘K" : "Ctrl+K";

/**
 * Replacement for retired page-level search bars: looks like a search input but
 * simply focuses the universal search in the left ribbon.
 */
export function UniversalSearchHint({ className }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={focusUniversalSearch}
      className={cn(
        "flex h-10 w-full max-w-xl items-center gap-2 rounded-lg border bg-background/80 px-3 text-sm text-muted-foreground",
        "hover:bg-accent/50 hover:text-foreground transition-colors",
        className
      )}
      data-testid="universal-search-hint"
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate text-left">Search everything...</span>
    </button>
  );
}

/** Render ts_headline output safely: everything is escaped except our own <mark> pairs. */
function renderSnippet(snippet: string) {
  const parts = snippet.split(/<mark>([\s\S]*?)<\/mark>/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="rounded-[2px] bg-primary/25 px-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}

type PanelView = "results" | "filters";

type DropdownPosition = { top: number; left: number; width: number };

export function SidebarUniversalSearch() {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [view, setView] = useState<PanelView>("results");
  const [filters, setFilters] = useState<UniversalSearchFilters>(DEFAULT_SEARCH_FILTERS);
  const [draft, setDraft] = useState<UniversalSearchFilters>(DEFAULT_SEARCH_FILTERS);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [, setLocation] = useLocation();
  const { state: sidebarState, setOpen: setSidebarOpen, isMobile } = useSidebar();
  const collapsed = sidebarState === "collapsed" && !isMobile;

  // 250ms debounce (no search on empty query)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const searchEnabled = debouncedQuery.length >= MIN_QUERY_LEN;
  const showPanel = panelOpen && (view === "filters" || query.trim().length > 0);

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["universal-search", debouncedQuery, filters],
    // react-query aborts the in-flight request via `signal` when the key changes
    queryFn: ({ signal }) => universalSearchService.search(debouncedQuery, filters, signal),
    enabled: searchEnabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const results = useMemo(() => data?.results ?? [], [data]);
  const threadResults = useMemo(() => results.filter((r) => r.resultType === "thread"), [results]);
  const docResults = useMemo(() => results.filter((r) => r.resultType === "document"), [results]);
  const flatResults = useMemo(() => [...threadResults, ...docResults], [threadResults, docResults]);
  const activeFilterCount = countActiveFilters(filters);

  // Filter panel option lists (fetched lazily when the panel opens)
  const { data: filterSpaces = [] } = useQuery({
    queryKey: ["universal-search-spaces"],
    queryFn: () => universalSearchService.listFilterSpaces(),
    enabled: showPanel && view === "filters",
    staleTime: 60_000,
  });
  const { data: filterTopics = [] } = useQuery({
    queryKey: ["universal-search-topics", draft.spaceIds],
    queryFn: () => universalSearchService.listFilterTopics(draft.spaceIds),
    enabled: showPanel && view === "filters",
    staleTime: 60_000,
  });

  const updateDropdownPosition = useCallback(() => {
    const el = inputWrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 380),
    });
  }, []);

  // While the panel is open, keep the portaled dropdown aligned with the input
  // (position is set eagerly by the handlers that open the panel).
  useLayoutEffect(() => {
    if (!showPanel) return;
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [showPanel, updateDropdownPosition]);

  /** Open the results panel, measuring the dropdown anchor first. */
  const openPanel = useCallback(() => {
    updateDropdownPosition();
    setPanelOpen(true);
  }, [updateDropdownPosition]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setPanelOpen(false);
        setView("results");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const focusInput = useCallback(() => {
    if (collapsed) {
      setSidebarOpen(true);
      // wait for the sidebar to expand before the input is focusable,
      // then re-measure once the expand transition has settled
      setTimeout(() => {
        inputRef.current?.focus();
        updateDropdownPosition();
      }, 120);
      setTimeout(updateDropdownPosition, 320);
    } else {
      inputRef.current?.focus();
    }
  }, [collapsed, setSidebarOpen, updateDropdownPosition]);

  // Global ⌘K / Ctrl+K + programmatic focus event
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (document.activeElement === inputRef.current) {
          setPanelOpen(false);
          setView("results");
        } else {
          focusInput();
          openPanel();
        }
      }
    };
    const onFocusEvent = () => {
      focusInput();
      openPanel();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener(UNIVERSAL_SEARCH_FOCUS_EVENT, onFocusEvent);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(UNIVERSAL_SEARCH_FOCUS_EVENT, onFocusEvent);
    };
  }, [focusInput, openPanel]);

  const openResult = useCallback(
    (result: UniversalSearchResult) => {
      setPanelOpen(false);
      setView("results");
      if (result.resultType === "thread") {
        setLocation(`/chat?threadId=${result.id}`);
      } else {
        setLocation(`/document/${result.id}`);
      }
    },
    [setLocation]
  );

  const clearSearch = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setPanelOpen(false);
    setView("results");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  const openFilters = useCallback(() => {
    setDraft(filters);
    setView("filters");
    openPanel();
  }, [filters, openPanel]);

  const applyFilters = useCallback(() => {
    setFilters(draft);
    setView("results");
    setActiveIndex(-1);
  }, [draft]);

  const resetFilters = useCallback(() => {
    setDraft(DEFAULT_SEARCH_FILTERS);
    setFilters(DEFAULT_SEARCH_FILTERS);
    setView("results");
    setActiveIndex(-1);
  }, []);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "f" && (e.metaKey || e.ctrlKey) && showPanel) {
        e.preventDefault();
        openFilters();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (view === "filters") {
          setView("results");
        } else if (showPanel) {
          setPanelOpen(false);
        } else {
          inputRef.current?.blur();
        }
        return;
      }
      if (view === "filters") return;
      const moveDown = e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey && showPanel);
      const moveUp = e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey && showPanel);
      if (moveDown || moveUp) {
        if (flatResults.length === 0) return;
        e.preventDefault();
        openPanel();
        setActiveIndex((prev) => {
          const next = moveDown
            ? (prev + 1) % flatResults.length
            : (prev - 1 + flatResults.length) % flatResults.length;
          document
            .getElementById(`universal-search-result-${next}`)
            ?.scrollIntoView({ block: "nearest" });
          return next;
        });
        return;
      }
      if (e.key === "Enter") {
        const target = flatResults[activeIndex] ?? flatResults[0];
        if (target && showPanel) {
          e.preventDefault();
          openResult(target);
        }
      }
    },
    [showPanel, view, flatResults, activeIndex, openFilters, openResult, openPanel]
  );

  const toggleDraftId = (key: "spaceIds" | "topicIds", id: string) => {
    setDraft((d) => {
      const list = d[key];
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      const updated = { ...d, [key]: next };
      // topics belonging to unselected spaces no longer apply
      if (key === "spaceIds" && next.length > 0) {
        updated.topicIds = updated.topicIds.filter((tid) =>
          filterTopics.some((t) => t.id === tid && next.includes(t.spaceId))
        );
      }
      return updated;
    });
  };

  const renderResultRow = (result: UniversalSearchResult, flatIndex: number) => {
    const Icon = result.resultType === "thread" ? MessageSquare : FileText;
    const breadcrumb = [result.spaceName, result.topicName].filter(Boolean).join(" › ");
    const when = formatWhen(result.updatedAt);
    return (
      <button
        key={`${result.resultType}-${result.id}`}
        id={`universal-search-result-${flatIndex}`}
        type="button"
        onClick={() => openResult(result)}
        onMouseEnter={() => setActiveIndex(flatIndex)}
        className={cn(
          "w-full flex items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors",
          flatIndex === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
        )}
        data-testid={`universal-search-result-${result.resultType}-${result.id}`}
      >
        <div
          className={cn(
            "mt-0.5 shrink-0 rounded p-1.5",
            result.resultType === "thread"
              ? "bg-green-500/10 text-green-600 dark:text-green-400"
              : "bg-rose-500/10 text-rose-600 dark:text-rose-400"
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{result.title}</div>
          {result.snippet && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {renderSnippet(result.snippet)}
            </p>
          )}
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground/80">
            {breadcrumb}
            {breadcrumb && when ? " · " : ""}
            {when}
          </p>
        </div>
      </button>
    );
  };

  const resultsView = (
    <>
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="truncate text-xs text-muted-foreground">
          {searchEnabled ? (
            <>
              Results for <span className="font-medium text-foreground">“{debouncedQuery}”</span>
              {data ? ` (${data.total})` : ""}
            </>
          ) : (
            `Type at least ${MIN_QUERY_LEN} characters to search`
          )}
        </span>
        <button
          type="button"
          onClick={clearSearch}
          className="ml-2 shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
          data-testid="universal-search-clear-header"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5">
        {isError && (
          <div className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground">
            <p>Search unavailable — try again</p>
            <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="universal-search-retry">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Retry
            </Button>
          </div>
        )}

        {!isError && isFetching && results.length === 0 && searchEnabled && (
          <div className="space-y-2 p-2" data-testid="universal-search-skeleton">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-2.5">
                <Skeleton className="h-7 w-7 rounded" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/5" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isError && !isFetching && searchEnabled && data && results.length === 0 && (
          <div className="px-3 py-5 text-sm" data-testid="universal-search-no-results">
            <p className="font-medium">No results for “{debouncedQuery}”</p>
            <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">
              <li>Check spelling</li>
              <li>Try fewer keywords</li>
              <li>Remove filters</li>
            </ul>
            {activeFilterCount > 0 && (
              <Button size="sm" variant="outline" className="mt-3" onClick={resetFilters}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {!isError && threadResults.length > 0 && (
          <div className="mb-1">
            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Threads ({threadResults.length})
            </div>
            {threadResults.map((r, i) => renderResultRow(r, i))}
          </div>
        )}
        {!isError && docResults.length > 0 && (
          <div className="mb-1">
            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Documents ({docResults.length})
            </div>
            {docResults.map((r, i) => renderResultRow(r, threadResults.length + i))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t px-3 py-1.5">
        <button
          type="button"
          onClick={openFilters}
          className="flex items-center gap-1.5 rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          data-testid="universal-search-open-filters"
        >
          <Settings className="h-3.5 w-3.5" />
          Filter results
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </button>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label="Search tips"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-xs">
            <p className="font-semibold">Search tips</p>
            <ul className="mt-1 space-y-0.5">
              <li>
                <code>hello world</code> — contains both words
              </li>
              <li>
                <code>hello or world</code> — contains either
              </li>
              <li>
                <code>"hello world"</code> — exact phrase
              </li>
              <li>
                <code>-world</code> — excludes “world”
              </li>
              <li>Partial words match automatically (frag → fragment)</li>
            </ul>
          </TooltipContent>
        </Tooltip>
      </div>
    </>
  );

  const filtersView = (
    <div className="p-3" data-testid="universal-search-filter-panel">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold">Search Filters</span>
        <button
          type="button"
          onClick={() => setView("results")}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          aria-label="Close filters"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Content Type</p>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={draft.threads}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, threads: v === true }))}
                data-testid="universal-search-filter-threads"
              />
              Threads
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={draft.documents}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, documents: v === true }))}
                data-testid="universal-search-filter-documents"
              />
              Documents
            </label>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Date Range</p>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Label htmlFor="universal-search-date-from" className="text-[11px] text-muted-foreground">
                From
              </Label>
              <Input
                id="universal-search-date-from"
                type="date"
                className="h-8 text-xs"
                value={draft.dateFrom}
                max={draft.dateTo || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="universal-search-date-to" className="text-[11px] text-muted-foreground">
                To
              </Label>
              <Input
                id="universal-search-date-to"
                type="date"
                className="h-8 text-xs"
                value={draft.dateTo}
                min={draft.dateFrom || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">
            Space {draft.spaceIds.length > 0 && `(${draft.spaceIds.length} selected)`}
          </p>
          <div className="max-h-28 overflow-y-auto rounded-md border p-1.5">
            {filterSpaces.length === 0 && (
              <p className="px-1 py-2 text-xs text-muted-foreground">No spaces</p>
            )}
            {filterSpaces.map((s) => (
              <label key={s.id} className="flex items-center gap-1.5 rounded px-1 py-1 text-xs hover:bg-accent/60">
                <Checkbox
                  checked={draft.spaceIds.includes(s.id)}
                  onCheckedChange={() => toggleDraftId("spaceIds", s.id)}
                />
                <span className="truncate">{s.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">
            Topic {draft.topicIds.length > 0 && `(${draft.topicIds.length} selected)`}
          </p>
          <div className="max-h-28 overflow-y-auto rounded-md border p-1.5">
            {filterTopics.length === 0 && (
              <p className="px-1 py-2 text-xs text-muted-foreground">No topics</p>
            )}
            {filterTopics.map((t) => (
              <label key={t.id} className="flex items-center gap-1.5 rounded px-1 py-1 text-xs hover:bg-accent/60">
                <Checkbox
                  checked={draft.topicIds.includes(t.id)}
                  onCheckedChange={() => toggleDraftId("topicIds", t.id)}
                />
                <span className="truncate">{t.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t pt-2.5">
        <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="universal-search-reset-filters">
          Reset filters
        </Button>
        <Button
          size="sm"
          onClick={applyFilters}
          disabled={!draft.threads && !draft.documents}
          data-testid="universal-search-apply-filters"
        >
          Apply
        </Button>
      </div>
    </div>
  );

  const dropdownContent = showPanel && dropdownPosition && (
    <div
      ref={dropdownRef}
      className="fixed z-50 overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width }}
      data-testid="universal-search-dropdown"
    >
      {view === "filters" ? filtersView : resultsView}
    </div>
  );

  // Icon-collapsed ribbon: a single search button that expands the sidebar and focuses the input
  if (collapsed) {
    return (
      <div className="flex justify-center px-0 py-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={() => {
                focusInput();
                openPanel();
              }}
              aria-label={`Search everything (${shortcutLabel})`}
              data-testid="universal-search-collapsed-button"
            >
              <Search className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Search everything ({shortcutLabel})</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="px-3 pb-1 pt-2">
      <div ref={inputWrapperRef} className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search everything..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
            setView("results");
            openPanel();
          }}
          onFocus={() => {
            if (query.trim()) openPanel();
          }}
          onKeyDown={handleInputKeyDown}
          className="h-9 rounded-lg pl-8 pr-16 text-sm"
          autoComplete="off"
          spellCheck={false}
          data-testid="input-universal-search"
          aria-label="Search everything"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {isFetching && searchEnabled ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : query ? (
            <button
              type="button"
              onClick={clearSearch}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
              data-testid="universal-search-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => (view === "filters" && showPanel ? setView("results") : openFilters())}
            className="relative rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Search filters"
            data-testid="universal-search-gear"
          >
            <Settings className="h-3.5 w-3.5" />
            {activeFilterCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>
      {typeof document !== "undefined" && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
